import React from 'react';

import { Alert, Modal } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';

import type { SubUnitType } from '@appTypes/esb';
import { useDeleteSubUnit } from '@modules/roster-v2/hooks/useSubUnitAdmin';
import type { RosterMemberView } from '@modules/roster-v2/models/rosterMemberView';

import styles from './DeleteSubUnitModal.module.scss';

export interface DeleteSubUnitModalViewProps {
  open: boolean;
  subUnitType: SubUnitType;
  subUnitName?: string;
  /** People to remove first; leave out while loading to show a spinner */
  memberCount?: number;
  deleting: boolean;
  /** Shows Retry */
  failed?: boolean;
  /** Members couldn't load: an error with Retry replaces the confirm */
  loadFailed?: boolean;
  onRetryLoad?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  afterClose?: () => void;
}

/** One Modal for spinner, load error and confirm, so it animates in once. */
export const DeleteSubUnitModalView: React.FC<
  DeleteSubUnitModalViewProps
> = props => {
  const {
    open,
    subUnitType,
    subUnitName,
    memberCount,
    deleting,
    failed = false,
    loadFailed = false,
    onRetryLoad,
    onConfirm,
    onCancel,
    afterClose,
  } = props;

  const name = <strong>{subUnitName}</strong>;

  let content: React.ReactNode = null;
  if (loadFailed) {
    content = (
      <Alert
        type="error"
        showIcon
        message={
          <FormattedMessage
            id={`rosterV2.subUnits.delete.loadFailed.${subUnitType}`}
          />
        }
        action={
          onRetryLoad && (
            <Button size="small" type="default" onClick={onRetryLoad}>
              <FormattedMessage id="rosterV2.subUnits.delete.retry" />
            </Button>
          )
        }
      />
    );
  } else if (memberCount !== undefined) {
    content = (
      <div className={styles.body}>
        {failed && (
          <Alert
            type="error"
            showIcon
            message={
              <FormattedMessage
                id={`rosterV2.subUnits.delete.failed.title.${subUnitType}`}
                values={{ name: subUnitName }}
              />
            }
            description={
              <FormattedMessage id="rosterV2.subUnits.delete.failed.description" />
            }
          />
        )}
        <Text>
          {memberCount > 0 ? (
            <FormattedMessage
              id={`rosterV2.subUnits.delete.body.${subUnitType}`}
              values={{ count: memberCount, name }}
            />
          ) : (
            <FormattedMessage
              id={`rosterV2.subUnits.delete.bodyEmpty.${subUnitType}`}
              values={{ name }}
            />
          )}
        </Text>
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      afterClose={afterClose}
      width={480}
      destroyOnClose
      loading={!content}
      maskClosable={!deleting}
      closable={!deleting}
      keyboard={!deleting}
      title={
        subUnitName ? (
          <FormattedMessage
            id={`rosterV2.subUnits.delete.title.${subUnitType}`}
            values={{ name: subUnitName }}
          />
        ) : undefined
      }
      footer={
        <div className={styles.footer}>
          <Button type="default" disabled={deleting} onClick={onCancel}>
            <FormattedMessage id="shared.cancel" />
          </Button>
          {!loadFailed && (
            <Button danger loading={deleting} onClick={onConfirm}>
              <FormattedMessage
                id={failed ? 'rosterV2.subUnits.delete.retry' : 'shared.delete'}
              />
            </Button>
          )}
        </div>
      }
    >
      {content}
    </Modal>
  );
};

interface DeleteSubUnitModalContainerProps {
  open: boolean;
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  members: readonly RosterMemberView[];
  onClose: () => void;
  /** Close animation ended: safe to unmount */
  afterClose?: () => void;
  onDeleted: () => void;
}

/**
 * Mount one per opening (new `key`) and unmount in `afterClose`: while
 * mounted, even closed, it rebuilds its delete arg when `members` changes.
 */
const DeleteSubUnitModalContainer: React.FC<
  DeleteSubUnitModalContainerProps
> = props => {
  const {
    open,
    orgGuid,
    subUnitType,
    subUnitId,
    members,
    onClose,
    afterClose,
    onDeleted,
  } = props;

  const { viewProps } = useDeleteSubUnit({
    open,
    orgGuid,
    subUnitType,
    subUnitId,
    members,
    onDeleted,
  });

  return (
    <DeleteSubUnitModalView
      {...viewProps}
      open={open}
      onCancel={onClose}
      afterClose={afterClose}
    />
  );
};

export default DeleteSubUnitModalContainer;
